import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { supabase } from '../integrations/supabase/client';
import { formatPhone } from '../utils/masks';
import { FichaCaptacao, FichaCaptacaoFormData } from '../types/ficha-captacao';

interface FichaCaptacaoFormProps {
  ficha?: FichaCaptacao;
  onSave: () => void;
  onCancel: () => void;
}

export const FichaCaptacaoForm: React.FC<FichaCaptacaoFormProps> = ({
  ficha,
  onSave,
  onCancel
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [formData, setFormData] = useState<FichaCaptacaoFormData>({
    status: 'rascunho',
    proprietario_nome: '',
    proprietario_telefone: '',
    proprietario_email: '',
    proprietario_cpf_cnpj: '',
    proprietario_endereco: '',
    imovel_endereco: '',
    imovel_numero: '',
    imovel_complemento: '',
    imovel_bairro: '',
    imovel_cidade: '',
    imovel_estado: 'SP',
    imovel_cep: '',
    imovel_tipo: '',
    imovel_categoria: '',
    imovel_dormitorios: 0,
    imovel_suites: 0,
    imovel_banheiros: 0,
    imovel_vagas: 0,
    imovel_area_construida: 0,
    imovel_area_total: 0,
    imovel_ano_construcao: new Date().getFullYear(),
    imovel_andar: '',
    imovel_torre: '',
    condominio_nome: '',
    condominio_valor: 0,
    condominio_amenidades: [],
    valor_venda: 0,
    valor_locacao: 0,
    valor_iptu_anual: 0,
    valor_iptu_mensal: 0,
    aceita_permuta: false,
    valor_permuta: 0,
    imovel_desocupado: true,
    data_desocupacao: '',
    aceita_financiamento: true,
    esta_financiado: false,
    banco_financiador: '',
    valor_financiado: 0,
    caracteristicas_especiais: '',
    observacoes: '',
    situacao_atual: '',
    motivo_venda: '',
    pressa_venda: false
  });

  useEffect(() => {
    if (ficha) {
      setFormData({
        status: ficha.status,
        proprietario_nome: ficha.proprietario_nome,
        proprietario_telefone: ficha.proprietario_telefone,
        proprietario_email: ficha.proprietario_email || '',
        proprietario_cpf_cnpj: ficha.proprietario_cpf_cnpj || '',
        proprietario_endereco: ficha.proprietario_endereco || '',
        imovel_endereco: ficha.imovel_endereco,
        imovel_numero: ficha.imovel_numero || '',
        imovel_complemento: ficha.imovel_complemento || '',
        imovel_bairro: ficha.imovel_bairro,
        imovel_cidade: ficha.imovel_cidade,
        imovel_estado: ficha.imovel_estado,
        imovel_cep: ficha.imovel_cep || '',
        imovel_tipo: ficha.imovel_tipo,
        imovel_categoria: ficha.imovel_categoria || '',
        imovel_dormitorios: ficha.imovel_dormitorios,
        imovel_suites: ficha.imovel_suites,
        imovel_banheiros: ficha.imovel_banheiros,
        imovel_vagas: ficha.imovel_vagas,
        imovel_area_construida: ficha.imovel_area_construida || 0,
        imovel_area_total: ficha.imovel_area_total || 0,
        imovel_ano_construcao: ficha.imovel_ano_construcao || new Date().getFullYear(),
        imovel_andar: ficha.imovel_andar || '',
        imovel_torre: ficha.imovel_torre || '',
        condominio_nome: ficha.condominio_nome || '',
        condominio_valor: ficha.condominio_valor,
        condominio_amenidades: ficha.condominio_amenidades || [],
        valor_venda: ficha.valor_venda,
        valor_locacao: ficha.valor_locacao,
        valor_iptu_anual: ficha.valor_iptu_anual,
        valor_iptu_mensal: ficha.valor_iptu_mensal,
        aceita_permuta: ficha.aceita_permuta,
        valor_permuta: ficha.valor_permuta || 0,
        imovel_desocupado: ficha.imovel_desocupado,
        data_desocupacao: ficha.data_desocupacao || '',
        aceita_financiamento: ficha.aceita_financiamento,
        esta_financiado: ficha.esta_financiado,
        banco_financiador: ficha.banco_financiador || '',
        valor_financiado: ficha.valor_financiado || 0,
        caracteristicas_especiais: ficha.caracteristicas_especiais || '',
        observacoes: ficha.observacoes || '',
        situacao_atual: ficha.situacao_atual || '',
        motivo_venda: ficha.motivo_venda || '',
        pressa_venda: ficha.pressa_venda,
        // Inicializar campos de display formatados
        valor_venda_display: ficha.valor_venda ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(ficha.valor_venda) : '',
        valor_locacao_display: ficha.valor_locacao ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(ficha.valor_locacao) : '',
        condominio_valor_display: ficha.condominio_valor ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(ficha.condominio_valor) : '',
        valor_iptu_anual_display: ficha.valor_iptu_anual ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(ficha.valor_iptu_anual) : ''
      });
    }
  }, [ficha]);

  // Função para formatar CEP
  const formatCep = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) {
      return numbers;
    }
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  // Função para buscar endereço por CEP
  const fetchAddressByCep = async () => {
    const cep = formData.imovel_cep?.replace(/\D/g, '');
    
    if (!cep || cep.length !== 8) {
      if (formData.imovel_cep && formData.imovel_cep.length > 0) {
        setCepError('CEP deve ter 8 dígitos');
      }
      return;
    }

    setCepLoading(true);
    setCepError('');

    try {
      // Tentar múltiplas URLs da API ViaCEP
      const urls = [
        `https://viacep.com.br/ws/${cep}/json/`,
        `https://cdn.apicep.com/file/apicep/${cep}.json`,
        `https://brasilapi.com.br/api/cep/v1/${cep}`
      ];
      
      let response;
      let data;
      
      // Tentar cada URL até uma funcionar
      for (const url of urls) {
        try {
          response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            }
          });
          
          if (response.ok) {
            data = await response.json();
            break;
          }
        } catch (urlError) {
          console.log(`Erro na URL ${url}:`, urlError);
          continue;
        }
      }
      
      if (!data) {
        throw new Error('Todas as APIs de CEP falharam');
      }
      
      // Verificar se o CEP foi encontrado (diferentes APIs têm formatos diferentes)
      if (data.erro || data.error) {
        setCepError('CEP não encontrado');
        return;
      }

      setFormData(prev => ({
        ...prev,
        imovel_estado: data.uf || data.state || prev.imovel_estado,
        imovel_cidade: data.localidade || data.city || prev.imovel_cidade,
        imovel_bairro: data.bairro || data.district || prev.imovel_bairro,
        imovel_endereco: data.logradouro || data.address || prev.imovel_endereco
      }));

      toast({
        title: "Endereço preenchido",
        description: "Dados do endereço foram preenchidos automaticamente."
      });

    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setCepError('CEP não encontrado ou serviço temporariamente indisponível');
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCep(e.target.value);
    setFormData({...formData, imovel_cep: formatted});
    setCepError('');
    
    // Auto-buscar quando CEP estiver completo
    const cleanCep = formatted.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      setTimeout(() => {
        fetchAddressByCep();
      }, 500); // Delay para evitar muitas requisições
    } else {
      // Clear any previous address data if CEP is incomplete
      if (cleanCep.length > 0 && cleanCep.length < 8) {
        setCepError('CEP deve ter 8 dígitos');
      } else {
        setCepError('');
      }
    }
  };

  const handleSubmit = async (status: 'rascunho' | 'definitivo') => {
    // Validar campos obrigatórios apenas quando salvar como definitivo
    if (status === 'definitivo') {
      if (!formData.proprietario_nome || !formData.proprietario_telefone || 
          !formData.imovel_endereco || !formData.imovel_bairro || 
          !formData.imovel_cidade || !formData.imovel_tipo) {
        toast({
          title: "Campos obrigatórios",
          description: "Preencha todos os campos obrigatórios para salvar como definitivo.",
          variant: "destructive"
        });
        return;
      }
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Limpar dados opcionais undefined para null
      // Remover campos de display antes de salvar
      const { 
        valor_venda_display, 
        valor_locacao_display, 
        condominio_valor_display, 
        valor_iptu_anual_display,
        ...formDataWithoutDisplay 
      } = formData;

      const cleanData = {
        ...formDataWithoutDisplay,
        status,
        user_id: user.id,
        // Converter undefined para null nos campos opcionais
        proprietario_email: formDataWithoutDisplay.proprietario_email || null,
        proprietario_cpf_cnpj: formDataWithoutDisplay.proprietario_cpf_cnpj || null,
        proprietario_endereco: formDataWithoutDisplay.proprietario_endereco || null,
        imovel_numero: formDataWithoutDisplay.imovel_numero || null,
        imovel_complemento: formDataWithoutDisplay.imovel_complemento || null,
        imovel_cep: formDataWithoutDisplay.imovel_cep || null,
        imovel_categoria: formDataWithoutDisplay.imovel_categoria || null,
        imovel_dormitorios: formDataWithoutDisplay.imovel_dormitorios || null,
        imovel_suites: formDataWithoutDisplay.imovel_suites || null,
        imovel_banheiros: formDataWithoutDisplay.imovel_banheiros || null,
        imovel_vagas: formDataWithoutDisplay.imovel_vagas || null,
        imovel_area_construida: formDataWithoutDisplay.imovel_area_construida || null,
        imovel_area_total: formDataWithoutDisplay.imovel_area_total || null,
        imovel_ano_construcao: formDataWithoutDisplay.imovel_ano_construcao || null,
        imovel_andar: formDataWithoutDisplay.imovel_andar || null,
        imovel_torre: formDataWithoutDisplay.imovel_torre || null,
        condominio_nome: formDataWithoutDisplay.condominio_nome || null,
        condominio_valor: formDataWithoutDisplay.condominio_valor || null,
        condominio_amenidades: formDataWithoutDisplay.condominio_amenidades?.length ? formDataWithoutDisplay.condominio_amenidades : null,
        valor_venda: formDataWithoutDisplay.valor_venda || null,
        valor_locacao: formDataWithoutDisplay.valor_locacao || null,
        valor_iptu_anual: formDataWithoutDisplay.valor_iptu_anual || null,
        valor_iptu_mensal: formDataWithoutDisplay.valor_iptu_mensal || null,
        valor_permuta: formDataWithoutDisplay.valor_permuta || null,
        data_desocupacao: formDataWithoutDisplay.data_desocupacao || null,
        banco_financiador: formDataWithoutDisplay.banco_financiador || null,
        valor_financiado: formDataWithoutDisplay.valor_financiado || null,
        caracteristicas_especiais: formDataWithoutDisplay.caracteristicas_especiais || null,
        observacoes: formDataWithoutDisplay.observacoes || null,
        situacao_atual: formDataWithoutDisplay.situacao_atual || null,
        motivo_venda: formDataWithoutDisplay.motivo_venda || null,
        // Campos booleanos devem ter valor padrão false se undefined
        aceita_permuta: formDataWithoutDisplay.aceita_permuta ?? false,
        imovel_desocupado: formDataWithoutDisplay.imovel_desocupado ?? false,
        aceita_financiamento: formDataWithoutDisplay.aceita_financiamento ?? false,
        esta_financiado: formDataWithoutDisplay.esta_financiado ?? false,
        pressa_venda: formDataWithoutDisplay.pressa_venda ?? false
      };

      if (ficha) {
        const { error } = await supabase
          .from('fichas_captacao')
          .update(cleanData)
          .eq('id', ficha.id);
        
        if (error) throw error;
        
        toast({
          title: "Ficha atualizada",
          description: `Ficha salva como ${status}.`
        });
      } else {
        const { error } = await supabase
          .from('fichas_captacao')
          .insert(cleanData);
        
        if (error) throw error;
        
        toast({
          title: "Ficha criada",
          description: `Ficha salva como ${status}.`
        });
      }

      onSave();
    } catch (error) {
      console.error('Erro ao salvar ficha:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar a ficha. Verifique os dados e tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header otimizado para mobile */}
      <div className="space-y-4">
        <div className="flex flex-col space-y-3">
          <h1 className="text-xl md:text-2xl font-bold text-center md:text-left">
            {ficha ? 'Editar Ficha de Captação' : 'Nova Ficha de Captação'}
          </h1>
          
          {/* Botões em layout mobile-first */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button 
              variant="outline" 
              onClick={onCancel}
              className="w-full sm:w-auto order-3 sm:order-1"
            >
              Cancelar
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleSubmit('rascunho')}
              disabled={loading}
              className="w-full sm:w-auto order-1 sm:order-2"
            >
              {loading ? 'Salvando...' : 'Salvar Rascunho'}
            </Button>
            <Button 
              onClick={() => handleSubmit('definitivo')}
              disabled={loading}
              className="w-full sm:w-auto order-2 sm:order-3 bg-orange-600 hover:bg-orange-700"
            >
              {loading ? 'Salvando...' : 'Salvar Definitivo'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Dados do Proprietário */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Dados do Proprietário</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={formData.proprietario_nome}
                onChange={(e) => setFormData({...formData, proprietario_nome: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone *</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={formatPhone(formData.proprietario_telefone)}
                onChange={(e) => setFormData({...formData, proprietario_telefone: e.target.value.replace(/\D/g, '')})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                className="w-full p-2 border rounded-md"
                value={formData.proprietario_email}
                onChange={(e) => setFormData({...formData, proprietario_email: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CPF/CNPJ</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={formData.proprietario_cpf_cnpj}
                onChange={(e) => setFormData({...formData, proprietario_cpf_cnpj: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Endereço</label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows={2}
                value={formData.proprietario_endereco}
                onChange={(e) => setFormData({...formData, proprietario_endereco: e.target.value})}
              />
            </div>
          </div>
        </Card>

        {/* Dados do Imóvel */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Dados do Imóvel</h3>
          <div className="space-y-4">
            {/* CEP primeiro campo */}
            <div>
              <label className="block text-sm font-medium mb-1">CEP *</label>
              <div className="relative">
                <input
                  type="text"
                  className={`w-full p-2 border rounded-md ${cepError ? 'border-red-500' : ''}`}
                  value={formData.imovel_cep}
                  onChange={handleCepChange}
                  placeholder="00000-000"
                  maxLength={9}
                />
                {cepLoading && (
                  <div className="absolute right-2 top-2">
                    <div className="animate-spin h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              {cepError && (
                <p className="text-red-500 text-sm mt-1">{cepError}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Endereço *</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={formData.imovel_endereco}
                onChange={(e) => setFormData({...formData, imovel_endereco: e.target.value})}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">Número</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={formData.imovel_numero}
                  onChange={(e) => setFormData({...formData, imovel_numero: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Complemento</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={formData.imovel_complemento}
                  onChange={(e) => setFormData({...formData, imovel_complemento: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">Bairro *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={formData.imovel_bairro}
                  onChange={(e) => setFormData({...formData, imovel_bairro: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cidade *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={formData.imovel_cidade}
                  onChange={(e) => setFormData({...formData, imovel_cidade: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <select
                className="w-full p-2 border rounded-md"
                value={formData.imovel_estado}
                onChange={(e) => setFormData({...formData, imovel_estado: e.target.value})}
              >
                <option value="SP">São Paulo</option>
                <option value="RJ">Rio de Janeiro</option>
                <option value="MG">Minas Gerais</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tipo *</label>
              <select
                className="w-full p-2 border rounded-md"
                value={formData.imovel_tipo}
                onChange={(e) => setFormData({...formData, imovel_tipo: e.target.value})}
                required
              >
                <option value="">Selecione...</option>
                <option value="Apartamento">Apartamento</option>
                <option value="Casa">Casa</option>
                <option value="Sobrado">Sobrado</option>
                <option value="Studio">Studio</option>
                <option value="Loft">Loft</option>
                <option value="Cobertura">Cobertura</option>
                <option value="Terreno">Terreno</option>
                <option value="Comercial">Comercial</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Características do Imóvel */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Características</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Dormitórios</label>
              <input
                type="number"
                min="0"
                className="w-full p-2 border rounded-md"
                value={formData.imovel_dormitorios || ''}
                onChange={(e) => setFormData({...formData, imovel_dormitorios: e.target.value === '' ? 0 : parseInt(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Suítes</label>
              <input
                type="number"
                min="0"
                className="w-full p-2 border rounded-md"
                value={formData.imovel_suites || ''}
                onChange={(e) => setFormData({...formData, imovel_suites: e.target.value === '' ? 0 : parseInt(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Banheiros</label>
              <input
                type="number"
                min="0"
                className="w-full p-2 border rounded-md"
                value={formData.imovel_banheiros || ''}
                onChange={(e) => setFormData({...formData, imovel_banheiros: e.target.value === '' ? 0 : parseInt(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vagas</label>
              <input
                type="number"
                min="0"
                className="w-full p-2 border rounded-md"
                value={formData.imovel_vagas || ''}
                onChange={(e) => setFormData({...formData, imovel_vagas: e.target.value === '' ? 0 : parseInt(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Área Construída (m²)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full p-2 border rounded-md"
                value={formData.imovel_area_construida || ''}
                onChange={(e) => setFormData({...formData, imovel_area_construida: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Área Total (m²)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full p-2 border rounded-md"
                value={formData.imovel_area_total || ''}
                onChange={(e) => setFormData({...formData, imovel_area_total: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
              />
            </div>
          </div>
        </Card>

        {/* Valores */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Valores</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Valor de Venda</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                placeholder="Digite o valor"
                value={formData.valor_venda_display || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const numericValue = value.replace(/\D/g, '');
                  
                  if (numericValue === '') {
                    setFormData({
                      ...formData, 
                      valor_venda: 0,
                      valor_venda_display: ''
                    });
                  } else {
                    // Converter para número em reais (não centavos)
                    const reaisValue = parseInt(numericValue) / 100;
                    const formatted = new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2
                    }).format(reaisValue);
                    
                    setFormData({
                      ...formData, 
                      valor_venda: reaisValue, // Salvar o valor real, não em centavos
                      valor_venda_display: formatted
                    });
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Valor de Locação</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                placeholder="Digite o valor"
                value={formData.valor_locacao_display || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const numericValue = value.replace(/\D/g, '');
                  
                  if (numericValue === '') {
                    setFormData({
                      ...formData, 
                      valor_locacao: 0,
                      valor_locacao_display: ''
                    });
                  } else {
                    // Converter para número em reais (não centavos)
                    const reaisValue = parseInt(numericValue) / 100;
                    const formatted = new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2
                    }).format(reaisValue);
                    
                    setFormData({
                      ...formData, 
                      valor_locacao: reaisValue, // Salvar o valor real, não em centavos
                      valor_locacao_display: formatted
                    });
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Condomínio</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                placeholder="Digite o valor"
                value={formData.condominio_valor_display || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const numericValue = value.replace(/\D/g, '');
                  
                  if (numericValue === '') {
                    setFormData({
                      ...formData, 
                      condominio_valor: 0,
                      condominio_valor_display: ''
                    });
                  } else {
                    // Converter para número em reais (não centavos)
                    const reaisValue = parseInt(numericValue) / 100;
                    const formatted = new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2
                    }).format(reaisValue);
                    
                    setFormData({
                      ...formData, 
                      condominio_valor: reaisValue, // Salvar o valor real, não em centavos
                      condominio_valor_display: formatted
                    });
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">IPTU Anual</label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                placeholder="Digite o valor"
                value={formData.valor_iptu_anual_display || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const numericValue = value.replace(/\D/g, '');
                  
                  if (numericValue === '') {
                    setFormData({
                      ...formData, 
                      valor_iptu_anual: 0,
                      valor_iptu_anual_display: ''
                    });
                  } else {
                    // Converter para número em reais (não centavos)
                    const reaisValue = parseInt(numericValue) / 100;
                    const formatted = new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2
                    }).format(reaisValue);
                    
                    setFormData({
                      ...formData, 
                      valor_iptu_anual: reaisValue, // Salvar o valor real, não em centavos
                      valor_iptu_anual_display: formatted
                    });
                  }
                }}
              />
            </div>
          </div>
        </Card>

        {/* Condições */}
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Condições e Situação</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="aceita_permuta"
                checked={formData.aceita_permuta}
                onChange={(e) => setFormData({...formData, aceita_permuta: e.target.checked})}
              />
              <label htmlFor="aceita_permuta" className="text-sm">Aceita Permuta</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="imovel_desocupado"
                checked={formData.imovel_desocupado}
                onChange={(e) => setFormData({...formData, imovel_desocupado: e.target.checked})}
              />
              <label htmlFor="imovel_desocupado" className="text-sm">Imóvel Desocupado</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="aceita_financiamento"
                checked={formData.aceita_financiamento}
                onChange={(e) => setFormData({...formData, aceita_financiamento: e.target.checked})}
              />
              <label htmlFor="aceita_financiamento" className="text-sm">Aceita Financiamento</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="esta_financiado"
                checked={formData.esta_financiado}
                onChange={(e) => setFormData({...formData, esta_financiado: e.target.checked})}
              />
              <label htmlFor="esta_financiado" className="text-sm">Está Financiado</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="pressa_venda"
                checked={formData.pressa_venda}
                onChange={(e) => setFormData({...formData, pressa_venda: e.target.checked})}
              />
              <label htmlFor="pressa_venda" className="text-sm">Pressa na Venda</label>
            </div>
          </div>
        </Card>

        {/* Observações */}
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Observações Adicionais</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Motivo da Venda</label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows={2}
                value={formData.motivo_venda}
                onChange={(e) => setFormData({...formData, motivo_venda: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Situação Atual</label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows={2}
                value={formData.situacao_atual}
                onChange={(e) => setFormData({...formData, situacao_atual: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Observações Gerais</label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows={3}
                value={formData.observacoes}
                onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};